/**
 * Patient-side bridge client.
 *
 * Two responsibilities:
 *   1. Seal a submission payload with the Mac's X25519 public key
 *      (libsodium crypto_box_seal — wire-compatible with the Python
 *      PyNaCl SealedBox decryption in bridge_sync.py).
 *   2. Provide HTTP helpers (redeem, submit) targeting either the
 *      same-origin bridge (production) or a configured BRIDGE_BASE_URL
 *      (local dev pointing at a bridge subprocess).
 *
 * Bridge-mode session state lives in localStorage under
 * `parseo_bridge_session` so it survives a refresh AND an accidental tab
 * close (patient goes to find their insurance card). A client-side expiry
 * stamp mirrors the bridge's 24h session-token TTL, so a stale session
 * self-clears instead of producing confusing 401s. The magic link itself
 * is also re-redeemable until it expires, so even a lost session only
 * costs the patient a re-tap of the same text/email link.
 */

import nacl from "tweetnacl";
import * as sealedbox from "tweetnacl-sealedbox-js";
import type { PatientPaymentStatus } from "../types/shared";
import {
  decodeBase64,
  encodeBase64,
  decodeUTF8,
} from "tweetnacl-util";

/** Bridge session state persisted in localStorage under SESSION_KEY. */
interface BridgeSession {
  sessionToken?: string;
  assignmentId?: string;
  symmetricKeyB64?: string;
  pubkeyB64?: string;
  _expiresAt?: number;
  [key: string]: unknown;
}

/** Error carrying HTTP status/detail off a failed bridge fetch. */
interface BridgeError extends Error {
  status?: number;
  detail?: string;
}

const SESSION_KEY = "parseo_bridge_session";

// Mirrors SESSION_TOKEN_TTL_SECONDS on the bridge — the token is dead
// server-side after this anyway, so keep the client copy in lockstep.
const SESSION_TTL_MS = 24 * 60 * 60 * 1000;

// Carry forward an active bridge session from the legacy keys: the
// pre-rebrand name, and the sessionStorage era (before the move to
// localStorage) so a patient mid-session isn't bounced by a deploy.
(function migrateLegacyBridgeSession() {
  try {
    if (localStorage.getItem(SESSION_KEY) == null) {
      const prior =
        sessionStorage.getItem(SESSION_KEY) ||
        sessionStorage.getItem("huggable_bridge_session");
      if (prior != null) localStorage.setItem(SESSION_KEY, prior);
    }
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem("huggable_bridge_session");
  } catch { /* storage unavailable — ignore */ }
})();

/** Base URL for bridge API calls. In production the bridge serves the SPA,
 *  so "" (same-origin) is correct. In local dev set VITE_BRIDGE_URL. */
const BRIDGE_BASE_URL = (import.meta.env.VITE_BRIDGE_URL || "").replace(/\/+$/, "");

/** Are we operating in bridge mode? */
export function isBridgeMode() {
  return Boolean(getBridgeSession());
}

// In-memory copy of the session, kept alongside localStorage. Private
// browsing, in-app webviews and lockdown configurations variously throw on
// storage access or purge it between page-load and the identity submit — and a
// swallowed storage failure here surfaced to a live plan member as "we
// couldn't check that just now, try again later" on every attempt, while the
// server (which never heard from her) sat ready to verify her first try
// (2026-09-17, Canada Life Zepbound plan-member leg). Memory carries the
// session for the lifetime of the page even when storage cannot; storage
// still carries it across reloads when it can.
let memorySession: BridgeSession | null = null;

/** Load the bridge session. Null if missing, corrupt, or past its expiry. */
export function getBridgeSession(): BridgeSession | null {
  let session: BridgeSession | null = null;
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw) session = JSON.parse(raw);
  } catch { /* storage unavailable — memory fallback below */ }
  if (!session) session = memorySession;
  if (!session) return null;
  if (session._expiresAt && Date.now() > session._expiresAt) {
    memorySession = null;
    try { localStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
    return null;
  }
  return session;
}

/** Persist the bridge session (survives tab close, self-expires with the
 *  server token). */
export function setBridgeSession(session: BridgeSession) {
  const record = { ...session, _expiresAt: session._expiresAt ?? Date.now() + SESSION_TTL_MS };
  memorySession = record;
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(record));
  } catch { /* storage blocked (private mode / webview) — memory carries it */ }
}

/** Clear the bridge session (called on submit success or on logout). */
export function clearBridgeSession() {
  memorySession = null;
  try { localStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
}

/**
 * Exchange a magic-link token for a session.
 *
 * Server returns `{ session_token, assignment_id, form_title, pubkey_b64,
 * identity_factors, identity_required, … }`. The patient's browser stores this
 * for the rest of the wizard flow.
 *
 * The session token returned here is UNVERIFIED — it opens nothing until
 * verifyIdentity() exchanges it. The expected identity hashes are deliberately
 * NOT in this payload any more (case imp_bad9596e): handing the browser the
 * answer made the check cosmetic, and an unsalted sha256 of a date of birth is
 * ~36,500 candidates, so it also gave away the date it protected.
 */
export async function redeemMagicLink(token: string) {
  const resp = await fetch(
    `${BRIDGE_BASE_URL}/api/bridge/magic/redeem?t=${encodeURIComponent(token)}`,
    { method: "GET", credentials: "omit" }
  );
  if (!resp.ok) {
    const body = await resp.text();
    const err: BridgeError = new Error(`redeem failed (${resp.status}): ${body.slice(0, 200)}`);
    err.status = resp.status;
    err.detail = body;
    throw err;
  }
  return resp.json();
}

/**
 * Seal a submission payload under the Mac's public key.
 *
 * Returns a base64 string ready to send as `ciphertext_b64` in
 * PUT /api/bridge/submissions/{assignment_id}.
 *
 * The plaintext bytes never leave this function. The ciphertext is
 * compatible with PyNaCl SealedBox(privkey).decrypt(ciphertext) on the
 * Mac.
 */
export function sealSubmission(payload: unknown, pubkeyB64: string) {
  if (!pubkeyB64) {
    throw new Error("sealSubmission: missing public key");
  }
  const pubkey = decodeBase64(pubkeyB64);
  if (pubkey.length !== nacl.box.publicKeyLength) {
    throw new Error(
      `sealSubmission: expected ${nacl.box.publicKeyLength}-byte pubkey, got ${pubkey.length}`
    );
  }
  const plaintextBytes = decodeUTF8(JSON.stringify(payload));
  const sealed = sealedbox.seal(plaintextBytes, pubkey);
  return encodeBase64(sealed);
}

// ── The form refused for its fee (ruling 2026-10-14, no free path) ─────────
//
// The bridge refuses the form itself — the sealed upload, the prepared PDF
// and the fields-schema — with a 409 whose `detail.code` says why
// (`bridge/routers/payments.py` `patient_fee_refusal`):
//   fee_required    the patient's row is `pending` with no card: save one,
//                   or choose "I have decided not to have this form filled out"
//   fee_declined    the patient decided not to have the form filled out
//   request_closed  any other signer (the plan member) of a declined
//                   request; neutral, it never says why
// The page routes on the code (BridgeOnboardingPage); every other failure
// stays the plain error it was.

export const FORM_REFUSAL_CODES = ["fee_required", "fee_declined", "request_closed"] as const;
export type FormRefusalCode = (typeof FORM_REFUSAL_CODES)[number];

function _refusalFromBody(status: number, body: unknown): FormRefusalCode | null {
  if (status !== 409 || !body || typeof body !== "object") return null;
  const d = (body as { detail?: unknown }).detail;
  const code = d && typeof d === "object" ? (d as { code?: unknown }).code : null;
  return typeof code === "string" && (FORM_REFUSAL_CODES as readonly string[]).includes(code)
    ? (code as FormRefusalCode)
    : null;
}

/** The refusal code a failed form fetch carries, or null. Reads a clone, so
 *  the caller's response body is untouched. Never throws. */
export async function formRefusalOf(resp: Response): Promise<FormRefusalCode | null> {
  if (resp.status !== 409) return null;
  try {
    const src = typeof resp.clone === "function" ? resp.clone() : resp;
    return _refusalFromBody(resp.status, await src.json());
  } catch {
    return null;
  }
}

/** An Error that says the bridge refused the form for its fee: the message
 *  keeps the caller's old wording, `refusal` carries the code. */
export function formRefusedError(message: string, code: FormRefusalCode): Error {
  const err = new Error(message) as Error & { refusal?: FormRefusalCode };
  err.refusal = code;
  return err;
}

/** The refusal code on an error thrown by a form fetch or the upload, or null. */
export function formRefusalCodeOf(err: unknown): FormRefusalCode | null {
  const code = err && typeof err === "object" ? (err as { refusal?: unknown }).refusal : null;
  return typeof code === "string" && (FORM_REFUSAL_CODES as readonly string[]).includes(code)
    ? (code as FormRefusalCode)
    : null;
}

/**
 * Submit the sealed ciphertext to the bridge.
 *
 * Uses the session token from sessionStorage to authenticate. The
 * bridge verifies the token is bound to the assignment_id supplied
 * in the URL. A fee refusal (409 `fee_required` / `fee_declined` /
 * `request_closed`) throws an error carrying the code (`formRefusalCodeOf`);
 * nothing was stored.
 */
export async function submitSealedToBridge(
  { assignmentId, ciphertextB64, schemaHash }:
    { assignmentId: string; ciphertextB64: string; schemaHash?: string }
) {
  const session = getBridgeSession();
  if (!session?.sessionToken) {
    throw new Error("submitSealedToBridge: no bridge session");
  }
  const resp = await fetch(
    `${BRIDGE_BASE_URL}/api/bridge/submissions/${encodeURIComponent(assignmentId)}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-Patient-Token": session.sessionToken,
      },
      credentials: "omit",
      body: JSON.stringify({
        ciphertext_b64: ciphertextB64,
        ...(schemaHash ? { schema_hash: schemaHash } : {}),
      }),
    }
  );
  if (!resp.ok) {
    const body = await resp.text();
    const message = `submit failed (${resp.status}): ${body.slice(0, 200)}`;
    let parsed: unknown = null;
    try {
      parsed = JSON.parse(body);
    } catch {
      /* not JSON */
    }
    const code = _refusalFromBody(resp.status, parsed);
    throw code ? formRefusedError(message, code) : new Error(message);
  }
  return resp.json();
}

/**
 * Prove you are the recipient, and upgrade the session.
 *
 * Until 2026-09-05 this comparison happened HERE, in the browser: redeem
 * handed the page the expected hash and `DobChallenge` compared it locally, so
 * "passing" was a React state transition no endpoint ever saw and possession of
 * the link was the whole credential (case imp_bad9596e). The bridge now holds
 * the expected value and answers.
 *
 * Still sends the DIGEST, not the date: the plaintext date of birth has never
 * crossed the wire and still doesn't.
 *
 * On success the stored session token is REPLACED with the verified one — every
 * PHI and submission endpoint requires it.
 */
export async function verifyIdentity(candidateHash: string): Promise<{
  verified: boolean;
  attempts_remaining: number;
  locked_out: boolean;
}> {
  const session = getBridgeSession();
  if (!session?.sessionToken) {
    throw new Error("no bridge session");
  }
  const resp = await fetch(`${BRIDGE_BASE_URL}/api/bridge/magic/verify-identity`, {
    method: "POST",
    credentials: "omit",
    headers: {
      "Content-Type": "application/json",
      "X-Patient-Token": session.sessionToken,
    },
    body: JSON.stringify({ candidate_hash: candidateHash }),
  });
  if (!resp.ok) {
    const body = await resp.text();
    const err: BridgeError = new Error(
      `identity check failed (${resp.status}): ${body.slice(0, 200)}`
    );
    err.status = resp.status;
    err.detail = body;
    throw err;
  }
  const data = await resp.json();
  if (data.verified && data.session_token) {
    setBridgeSession({ ...session, sessionToken: data.session_token });
  }
  return {
    verified: !!data.verified,
    attempts_remaining: Number(data.attempts_remaining ?? 0),
    locked_out: !!data.locked_out,
  };
}

/**
 * sha256-hex of a string. Used to compute the candidate the bridge checks the
 * recipient's answer against — see verifyIdentity. Native Web Crypto so
 * no extra dep.
 */
export async function sha256Hex(text: string) {
  const bytes = new TextEncoder().encode(text);
  const buf = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * sha256-hex of raw BYTES (as opposed to sha256Hex, which hashes a string).
 *
 * Used by the prescriber signing leg to fingerprint the exact decrypted PDF
 * the physician reviewed — `served_pdf_sha256` in the sealed submission. That
 * hash is the non-repudiation anchor: the Mac compares it against the hash it
 * recorded when it dispatched the document, so "the physician signed THIS
 * version of the form" is a checkable claim rather than an assertion.
 */
export async function sha256HexBytes(bytes: Uint8Array): Promise<string> {
  // Copy into a plainly ArrayBuffer-backed view first: `BufferSource` excludes
  // SharedArrayBuffer-backed views, and a bare `Uint8Array` is generic over
  // ArrayBufferLike. The copy costs one pass over the PDF and nothing else.
  const view = new Uint8Array(bytes.length);
  view.set(bytes);
  const buf = await crypto.subtle.digest("SHA-256", view);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// ── Signer PIN setup (one-time link, no session) ──────────────────────────
//
// The prescriber's signing PIN is SELF-SET and never operator-known: the
// operator texts a single-use setup link (~30 min) while with the physician,
// and the physician chooses the PIN on their own device. Only the digest
// crosses the wire, and the server stores only a hash of that. Operator
// ignorance of the PIN is what gives a link signature its evidentiary weight
// — see docs/pilot/MODEL_A_PILOT_PLAN.md §3.3.
//
// Neither call carries auth headers or a bridge session: the token in the URL
// IS the credential, and the person setting a PIN does not have a session yet.

/** Check a PIN-setup token before showing the form. 401 = unknown/expired/used. */
export async function pinSetupRedeem(
  token: string
): Promise<{ ok: boolean; expires_at?: string }> {
  const resp = await fetch(
    `${BRIDGE_BASE_URL}/api/bridge/signers/pin-setup/redeem?t=${encodeURIComponent(token)}`,
    { method: "GET", credentials: "omit" }
  );
  if (!resp.ok) {
    // Never echo the token back into the message — it is the credential.
    const err: BridgeError = new Error(`pin setup redeem failed (${resp.status})`);
    err.status = resp.status;
    throw err;
  }
  return resp.json();
}

/**
 * Set the signer's PIN. `pinDigest` is 64 lowercase hex (see lib/signerPin);
 * the PIN itself never leaves the device.
 *
 * A 422 carries the server's human-readable weak-PIN reason on `err.detail`,
 * which the setup page shows verbatim — the server is authoritative about
 * which PINs it will accept, and paraphrasing it would drift.
 */
export async function pinSetupSubmit(
  token: string,
  pinDigest: string
): Promise<{ ok: boolean }> {
  const resp = await fetch(`${BRIDGE_BASE_URL}/api/bridge/signers/pin-setup/submit`, {
    method: "POST",
    credentials: "omit",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ t: token, pin_digest: pinDigest }),
  });
  if (!resp.ok) {
    let detail = "";
    try {
      const body = await resp.json();
      detail = typeof body?.detail === "string" ? body.detail : "";
    } catch {
      detail = "";
    }
    const err: BridgeError = new Error(`pin setup submit failed (${resp.status})`);
    err.status = resp.status;
    if (detail) err.detail = detail;
    throw err;
  }
  return resp.json();
}

// ── Symmetric (secretbox) — for content going FROM Mac TO patient ─────────
//
// Sealed-box is one-way: anyone with the pubkey can encrypt, only the
// privkey-holder can decrypt. That works for patient → Mac (the patient
// has the Mac's pubkey, only the Mac can read submissions) but it does
// NOT work for Mac → patient because the patient has no privkey.
//
// For PDFs and other content going TO the patient, we use libsodium's
// secretbox (XSalsa20-Poly1305) with a fresh 32-byte symmetric key per
// assignment. The Mac generates the key, encrypts the PDF, and puts the
// key in the URL fragment of the magic link. URL fragments are NEVER
// sent in HTTP requests (HTTP spec), so the bridge never receives the
// key. The SPA reads the fragment, decrypts the PDF locally.
//
// Wire format: nonce(24 bytes) || ciphertext. base64-encoded.

const SECRETBOX_NONCE_BYTES = 24;
const SECRETBOX_KEY_BYTES = 32;

/** Decrypt a base64 ciphertext envelope with a base64 symmetric key. */
export function openSecretBox(envelopeB64: string, keyB64: string): Uint8Array {
  const key = decodeBase64(keyB64);
  if (key.length !== SECRETBOX_KEY_BYTES) {
    throw new Error(
      `openSecretBox: expected ${SECRETBOX_KEY_BYTES}-byte key, got ${key.length}`
    );
  }
  const envelope = decodeBase64(envelopeB64);
  if (envelope.length < SECRETBOX_NONCE_BYTES + nacl.secretbox.overheadLength) {
    throw new Error("openSecretBox: envelope too short");
  }
  const nonce = envelope.subarray(0, SECRETBOX_NONCE_BYTES);
  const ciphertext = envelope.subarray(SECRETBOX_NONCE_BYTES);
  const plaintext = nacl.secretbox.open(ciphertext, nonce, key);
  if (!plaintext) {
    throw new Error("openSecretBox: decryption failed (wrong key or corrupted)");
  }
  return plaintext;
}

/** Encrypt a Uint8Array with a base64 symmetric key. Returns base64 envelope. */
export function sealSecretBox(plaintextBytes: Uint8Array, keyB64: string) {
  const key = decodeBase64(keyB64);
  if (key.length !== SECRETBOX_KEY_BYTES) {
    throw new Error(
      `sealSecretBox: expected ${SECRETBOX_KEY_BYTES}-byte key, got ${key.length}`
    );
  }
  const nonce = nacl.randomBytes(SECRETBOX_NONCE_BYTES);
  const ciphertext = nacl.secretbox(plaintextBytes, nonce, key);
  const envelope = new Uint8Array(SECRETBOX_NONCE_BYTES + ciphertext.length);
  envelope.set(nonce, 0);
  envelope.set(ciphertext, SECRETBOX_NONCE_BYTES);
  return encodeBase64(envelope);
}

/** Generate a fresh 32-byte symmetric key, returned as base64. */
export function generateSymmetricKey() {
  return encodeBase64(nacl.randomBytes(SECRETBOX_KEY_BYTES));
}

/**
 * Fetch the encrypted patient profile from the bridge and decrypt it with
 * the session's symmetric key. Returns the decrypted profile (Mac-side
 * snake_case keys) or null when no profile is on file for this assignment.
 *
 * The bridge stores the ciphertext as an opaque blob; only the SPA holds
 * the key (it lives in the URL fragment from the magic link).
 */
export async function fetchEncryptedProfile() {
  const session = getBridgeSession();
  if (!session?.sessionToken || !session?.assignmentId || !session?.symmetricKeyB64) {
    return null;
  }
  const resp = await fetch(
    `${BRIDGE_BASE_URL}/api/bridge/assignments/${encodeURIComponent(session.assignmentId)}/profile`,
    {
      method: "GET",
      headers: { "X-Patient-Token": session.sessionToken },
      credentials: "omit",
    }
  );
  if (!resp.ok) return null;
  const body = await resp.json();
  if (!body?.encrypted_b64) return null;
  try {
    const plaintext = openSecretBox(body.encrypted_b64, session.symmetricKeyB64);
    return JSON.parse(new TextDecoder().decode(plaintext));
  } catch {
    return null;
  }
}

/**
 * Fetch the encrypted coverage-inquiry payload for a coverage assignment
 * (kind='coverage_inquiry' — no PDF/fields schema behind it). Returns the
 * bridge body `{ encrypted_coverage_b64 }`; the caller decrypts it with
 * the session's symmetric key (see decodeCoveragePayload in
 * coverageInquiry.js). The bridge marks `opened_at` on this call.
 */
export async function fetchCoverage(assignmentId: string) {
  const session = getBridgeSession();
  if (!session?.sessionToken) {
    throw new Error("fetchCoverage: no bridge session");
  }
  const resp = await fetch(
    `${BRIDGE_BASE_URL}/api/bridge/assignments/${encodeURIComponent(assignmentId)}/coverage`,
    {
      method: "GET",
      headers: { "X-Patient-Token": session.sessionToken },
      credentials: "omit",
    }
  );
  if (!resp.ok) {
    const body = await resp.text();
    const err: BridgeError = new Error(`coverage fetch failed (${resp.status}): ${body.slice(0, 200)}`);
    err.status = resp.status;
    throw err;
  }
  return resp.json();
}

/**
 * Submit the sealed coverage reply to the bridge. `ciphertextB64` is the
 * sealed-box output of sealSubmission(replyPayload, session.pubkeyB64).
 * The bridge 409s on a duplicate reply for the same assignment.
 */
export async function submitCoverageReply(assignmentId: string, ciphertextB64: string) {
  const session = getBridgeSession();
  if (!session?.sessionToken) {
    throw new Error("submitCoverageReply: no bridge session");
  }
  const resp = await fetch(
    `${BRIDGE_BASE_URL}/api/bridge/assignments/${encodeURIComponent(assignmentId)}/coverage-reply`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Patient-Token": session.sessionToken,
      },
      credentials: "omit",
      body: JSON.stringify({ ciphertext_b64: ciphertextB64 }),
    }
  );
  if (!resp.ok) {
    const body = await resp.text();
    const err: BridgeError = new Error(`coverage reply failed (${resp.status}): ${body.slice(0, 200)}`);
    err.status = resp.status;
    throw err;
  }
  return resp.json();
}

/** Parse a magic-link URL with the symmetric key in the fragment.
 *  Returns `{ token, key }` or null when the URL doesn't carry both. */
export function parseMagicLinkUrl(href: string): { token: string; key: string } | null {
  try {
    const url = new URL(href);
    const m = url.pathname.match(/^\/r\/([^/]+)/);
    if (!m) return null;
    const token = decodeURIComponent(m[1]);
    const key = url.hash.startsWith("#") ? url.hash.slice(1) : "";
    if (!token || !key) return null;
    return { token, key };
  } catch {
    return null;
  }
}

// ── Patient payments (bridge payments router, slice 3b) ───────────────────
//
// The fee step's server calls. Every function maps 404 and 503 to a typed
// result rather than throwing, because both are ordinary answers here:
// 404 = no payment row for this form (shadow sends, no-fee sends, previews);
// the bridge answers that case with `{"detail":{"code":"no_payment_row"}}`, so
// a 404 WITHOUT that code (a mis-mounted router, a wrong path) is
// distinguishable in logs and in the `reason` the post helpers return — the
// link still treats both as "no row → skip". 503 = payments not configured
// (a paused row is a 200 with `paused: true`). The fee step renders ONLY on a
// 200 whose status is `pending` and not paused; every other answer means "go
// to the form".
//
// Path id: the contract names the segment `{editor_assignment_id}`, but a
// patient session only knows the bridge assignment id it was minted for
// (redeem returns `assignment_id`, and the session token is bound to it). So
// the link sends `session.assignmentId`; the bridge router resolves it to the
// row's editor assignment id from its own assignment row.

export type PaymentStatusResult =
  | { kind: "ok"; payment: PatientPaymentStatus }
  | { kind: "none" }
  | { kind: "unavailable"; reason: string }
  | { kind: "error"; status: number };

/** A post's answer. On failure `status` is the HTTP status, `reason` the
 *  bridge's `detail.code` (or `http_<status>`), and `rowStatus` the payment
 *  row's status when the bridge sent one with the refusal (`detail.status`,
 *  e.g. a 409 `not_declinable` / `not_open` / `not_pending`), so the fee step
 *  can tell a card already saved from a form already on its way. */
export type PaymentPostResult<T> =
  | { ok: true; body: T }
  | { ok: false; status: number; reason: string; rowStatus?: string };

function _paymentsUrl(suffix: string): string | null {
  const session = getBridgeSession();
  if (!session?.sessionToken || !session?.assignmentId) return null;
  return `${BRIDGE_BASE_URL}/api/bridge/payments/${encodeURIComponent(session.assignmentId)}/${suffix}`;
}

function _paymentsHeaders(json: boolean): Record<string, string> {
  const session = getBridgeSession();
  const h: Record<string, string> = { "X-Patient-Token": (session?.sessionToken as string) || "" };
  if (json) h["Content-Type"] = "application/json";
  return h;
}

/** `detail.code` (or a string `detail`) and `detail.status` of a refusal. */
async function _detailOf(resp: Response): Promise<{ reason: string; rowStatus?: string }> {
  try {
    const body = await resp.json();
    const d = body?.detail;
    if (typeof d === "string") return { reason: d };
    if (d && typeof d.code === "string") {
      return typeof d.status === "string" ? { reason: d.code, rowStatus: d.status } : { reason: d.code };
    }
  } catch { /* not JSON */ }
  return { reason: `http_${resp.status}` };
}

async function _reasonOf(resp: Response): Promise<string> {
  return (await _detailOf(resp)).reason;
}

/** GET …/status. Never throws. */
export async function fetchPaymentStatus(): Promise<PaymentStatusResult> {
  const url = _paymentsUrl("status");
  if (!url) return { kind: "none" };
  let resp: Response;
  try {
    resp = await fetch(url, { method: "GET", headers: _paymentsHeaders(false), credentials: "omit" });
  } catch {
    return { kind: "error", status: 0 };
  }
  if (resp.status === 404) return { kind: "none" };
  if (resp.status === 503) return { kind: "unavailable", reason: await _reasonOf(resp) };
  if (!resp.ok) return { kind: "error", status: resp.status };
  try {
    return { kind: "ok", payment: (await resp.json()) as PatientPaymentStatus };
  } catch {
    return { kind: "error", status: resp.status };
  }
}

async function _postPayment<T>(suffix: string, body?: unknown): Promise<PaymentPostResult<T>> {
  const url = _paymentsUrl(suffix);
  if (!url) return { ok: false, status: 0, reason: "no_session" };
  let resp: Response;
  try {
    resp = await fetch(url, {
      method: "POST",
      headers: _paymentsHeaders(body !== undefined),
      credentials: "omit",
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    return { ok: false, status: 0, reason: "network" };
  }
  if (!resp.ok) return { ok: false, status: resp.status, ...(await _detailOf(resp)) };
  try {
    return { ok: true, body: (await resp.json()) as T };
  } catch {
    return { ok: true, body: {} as T };
  }
}

/** POST …/consent — the fee notice the patient accepted: its version, the
 *  sha256 of `[...text_blocks, consent_text].join("\n\n")`, and the amount
 *  shown. The bridge recomputes the hash from what it served. */
export function postPaymentConsent(version: string, noticeHash: string, totalCents: number) {
  return _postPayment<{ recorded_at: string }>("consent", {
    version,
    notice_hash: noticeHash,
    total_cents: totalCents,
  });
}

/** POST …/setup — a pay-page URL carrying a one-time token in its fragment.
 *  The bridge allows it from `pending` and `deferred` (save a card) and from
 *  `charge_failed` (pay now: a payment-mode Checkout). */
export function postPaymentSetup() {
  return _postPayment<{ pay_url: string; expires_at: string }>("setup");
}

export type WaiverReason = "cannot_afford" | "other";

/** POST …/waiver — "I can't pay right now". The note is capped at 200. */
export function postPaymentWaiver(reason?: WaiverReason, note?: string) {
  const body: { reason?: WaiverReason; note?: string } = {};
  if (reason) body.reason = reason;
  if (note && note.trim()) body.note = note.trim().slice(0, 200);
  return _postPayment<{ status: "waiver_requested" }>("waiver", body);
}

/** POST …/question — "I have a question". The note is capped at 200. */
export function postPaymentQuestion(note?: string) {
  const body: { note?: string } = {};
  if (note && note.trim()) body.note = note.trim().slice(0, 200);
  return _postPayment<{ status: "question" }>("question", body);
}

/** POST …/defer — "Not now". */
export function postPaymentDefer() {
  return _postPayment<{ status: "deferred" }>("defer");
}

/** POST …/decline — "I have decided not to have this form filled out"
 *  (ruling 2026-10-14; wording G20, 2026-10-15). Parseo prepares and sends
 *  nothing and nothing is charged; the row
 *  becomes `declined_old_way`, terminal. Allowed from `pending`, and from
 *  `card_saved` while nothing has been released, until the patient's sealed
 *  form is accepted or a send is locked in; otherwise 409 `not_declinable`
 *  with the row's status (`rowStatus`). A second tap answers 200 again.
 *  Works while Stripe is down (the bridge does not need it to decline). */
export function postPaymentDecline() {
  return _postPayment<{ status: string }>("decline");
}

/** POST …/refresh — "I've saved my card": the bridge asks Stripe now rather
 *  than waiting for the webhook, and answers the row's status. At most 10
 *  per 10 minutes (429 `too_many_refreshes`). */
export function postPaymentRefresh() {
  return _postPayment<{ status: string }>("refresh");
}
